import { timestampDate, type Timestamp } from '@bufbuild/protobuf/wkt';

import { buildFindParkingUrl, eventEndOrDefault } from './buildParkingUrls';
import {
  EVENT_TIMEZONE_FALLBACK,
  eventMonthKey,
  formatEventIsoDate,
  formatEventMonthLabel,
  formatEventTimeLabel,
} from './formatEventDateTime';
import { formatEventParkingLabel } from './formatEventParkingLabel';

import type { EventRowGroupProps, EventRowItem } from '@/components/event-row';
import type { LatLng } from '@m-service/engagement-api/google/type/latlng_pb';
import type { Event } from '@m-service/engagement-api/metropolis/engagement/marketing_page/v1/marketing_page_pb';

export interface MapVenueEventsToRowsOptions {
  /**
   * Venue coordinates + name used to build a per-event Find Parking deep link
   * that includes that event's start/end window.
   */
  coordinates?: LatLng;
  /**
   * Per-event landing page slug, keyed by `eventId`. When present, the
   * resulting row gets `href="/events/{slug}"` so the "Find parking" button
   * renders as a Next `Link` (server-safe — no function prop required).
   */
  eventSlugById?: Record<string, string>;
  /**
   * Resolved lot IANA timezone for Find Parking `start`/`end` wall-clocks.
   * When omitted, per-event links keep coords/location only (no times).
   */
  eventWindowTimeZone?: string;
  /**
   * Fallback row href used when an event has no times (and no landing-page
   * slug). Without an `href` the "Find parking" button does not render.
   */
  findParkingHref?: string;
  location?: string;
  /**
   * Per-event parking label override, keyed by `eventId` (e.g. "Parking
   * starting at $13"). When omitted for an event, the label is derived from the
   * event's `rateStartsAt`.
   */
  parkingLabelById?: Record<string, string>;
  /** Timezone for display labels (may use the UTC display fallback). */
  timeZone?: string;
  /**
   * Fallback venue id when an event record omits `venueId` (common on venue
   * pages, where the page already knows the venue).
   */
  venueId?: string;
}

/**
 * A single venue event projected to a serializable row. Extends `EventRowItem`
 * with the month grouping fields so a client component can re-group a sliced or
 * filtered subset without re-reading the proto `Timestamp`s.
 */
export interface VenueEventRow extends EventRowItem {
  /** Stable `yyyy-MM` key used to group rows by calendar month. */
  monthKey: string;
  /** Display label for the month group, e.g. "February 2026". */
  monthLabel: string;
}

function startMs(start: Timestamp | undefined): number {
  if (!start) return Number.POSITIVE_INFINITY;
  return timestampDate(start).getTime();
}

/**
 * Sorts venue events chronologically and projects them to flat, serializable
 * `VenueEventRow`s. Events with no `startTime` are dropped — they have no row to
 * render. The output is safe to hand to a client component (all values are
 * strings) for client-side date filtering and infinite scroll.
 */
export function mapVenueEventsToRows(
  events: Event[],
  {
    eventSlugById,
    parkingLabelById,
    findParkingHref,
    coordinates,
    location,
    timeZone = EVENT_TIMEZONE_FALLBACK,
    eventWindowTimeZone,
    venueId,
  }: MapVenueEventsToRowsOptions = {},
): VenueEventRow[] {
  return [...events]
    .filter((e) => !!e.startTime)
    .sort((a, b) => startMs(a.startTime) - startMs(b.startTime))
    .map((event) => {
      const start = event.startTime!;
      const slug = eventSlugById?.[event.eventId];
      const eventFindParkingHref = buildFindParkingUrl({
        coordinates,
        location,
        target: {
          eventId: event.eventId,
          type: 'event',
          venueId: event.venueId || venueId || undefined,
        },
        ...(eventWindowTimeZone
          ? {
              start: event.startTime,
              end: eventEndOrDefault(start, event.endTime),
              timeZone: eventWindowTimeZone,
            }
          : {}),
      });
      return {
        title: event.name,
        date: formatEventIsoDate(start, timeZone),
        timeLabel: formatEventTimeLabel(start, timeZone),
        parkingLabel:
          parkingLabelById?.[event.eventId] ?? formatEventParkingLabel(event.rateStartsAt),
        // Prefer an explicit event landing-page slug when the caller provides one;
        // otherwise deep-link Find Parking (with times only when TZ resolved).
        href: slug ? `/events/${slug}` : eventFindParkingHref || findParkingHref,
        monthKey: eventMonthKey(start, timeZone),
        monthLabel: formatEventMonthLabel(start, timeZone),
      };
    });
}

/**
 * Groups already-mapped, chronologically-sorted rows into `EventRowGroupProps`
 * for `<EventList />`. Because the rows arrive sorted, each month's rows are
 * contiguous and groups preserve chronological order.
 */
export function groupVenueEventRows(rows: VenueEventRow[]): EventRowGroupProps[] {
  const groupsByKey = new Map<string, EventRowGroupProps>();
  for (const row of rows) {
    let group = groupsByKey.get(row.monthKey);
    if (!group) {
      group = { monthLabel: row.monthLabel, items: [] };
      groupsByKey.set(row.monthKey, group);
    }
    group.items.push(row);
  }
  return Array.from(groupsByKey.values());
}
